/**
 * Conversion between the normalized editable question model and the
 * questionnaire-template API payload. Shared by the create-study wizard and
 * the template editor so both speak the same dialect.
 *
 * The backend model only knows TEXT / SINGLE_CHOICE / MULTIPLE_CHOICE, so
 * richer kinds degrade to the closest one: scale → single 1–5, nps → single
 * 0–10, rank → multi with its own options. On load, the generated numeric
 * scales are recognized and restored to their original kind.
 */
import { newId, type EditableQuestion } from './types'

export type ApiQuestionType = 'TEXT' | 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE'

export interface ApiQuestion {
  id?: number
  text: string
  question_type: ApiQuestionType
  section_name: string
  choices: Array<{ value: number; text: string; skip_questions?: number }>
}

const numbered = (from: number, to: number) =>
  Array.from({ length: to - from + 1 }, (_, i) => ({ value: from + i, text: String(from + i) }))

export function editableToApiQuestion(q: EditableQuestion): ApiQuestion {
  const own = q.options
    .filter((o) => o.text.trim())
    .map((o, i) => ({ value: o.value ?? i + 1, text: o.text.trim() }))
  const base = { text: q.text.trim(), section_name: q.section ?? '' }
  switch (q.kind) {
    case 'text':
      return { ...base, question_type: 'TEXT', choices: [] }
    case 'scale':
      return { ...base, question_type: 'SINGLE_CHOICE', choices: numbered(1, 5) }
    case 'nps':
      return { ...base, question_type: 'SINGLE_CHOICE', choices: numbered(0, 10) }
    case 'multi':
    case 'rank':
      return { ...base, question_type: 'MULTIPLE_CHOICE', choices: own }
    default: // single
      return { ...base, question_type: 'SINGLE_CHOICE', choices: own }
  }
}

/** True when choices are exactly the generated numeric run `from..to`. */
function isNumericRun(choices: ApiQuestion['choices'], from: number, to: number): boolean {
  if (choices.length !== to - from + 1) return false
  return choices.every((ch, i) => ch.text === String(from + i) && ch.value === from + i)
}

export function apiQuestionToEditable(q: ApiQuestion): EditableQuestion {
  const choices = q.choices ?? []
  let kind: EditableQuestion['kind']
  let options = choices.map((ch, i) => ({ id: newId('opt'), text: ch.text, value: ch.value ?? i + 1 }))
  if (q.question_type === 'TEXT') {
    kind = 'text'
    options = []
  } else if (q.question_type === 'MULTIPLE_CHOICE') {
    kind = 'multi'
  } else if (isNumericRun(choices, 1, 5)) {
    kind = 'scale'
    options = []
  } else if (isNumericRun(choices, 0, 10)) {
    kind = 'nps'
    options = []
  } else {
    kind = 'single'
  }
  return {
    id: q.id != null ? String(q.id) : newId('q'),
    kind,
    text: q.text ?? '',
    required: true,
    section: q.section_name ?? '',
    options,
  }
}
